import { useCallback } from 'react'

import { OrderBookColumnOrder } from '@ebalance/proto/bffuser/v1/setting_model_pb'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import type {
  BoardColumnOrder,
  BoardColumnOrderControl,
} from '~/pages/IntradayBidding/components/BoardColumnOrderToggle'
import { usePersonalSettings } from '~/pages/Settings/hooks/usePersonalSettings'

type UseBoardColumnOrderResult = {
  /**
   * `IntradayBoardInterconPanel.columnOrder` にそのまま渡す。 `isSettling` は個人設定の取得が
   * 決着するまで true (D8)。 売り左で先に描いて後から反転すると、 反転の直前に見た位置で価格セルを
   * 押し、 逆側の札作成を始める誤操作が起こりうるため、 板のテーブルは初回はこの間、 読み込み表示の
   * ままにする。 取得に失敗した場合も「決着」 (= false) で、 板は売り左で表示する (D7)。
   */
  columnOrder: BoardColumnOrderControl
}

/**
 * 板発注タブの売買列表示順 (AC-SPS-032 / AC-SPS-033 / AC-BIN-026) の薄い hook。
 *
 * 設定画面と同じ `usePersonalSettings` の保存経路 (同じ query key のキャッシュ・楽観更新・
 * rollback・全項目置換の payload 組み立て) を通し、 板側は「現在の向き・切替可否・切替」
 * だけを受け取る (D5)。
 *
 * - 取得失敗でも板発注タブごと全画面エラーにしない: `throwOnError: false` (D7)。
 * - 設定未取得 (取得中 / 失敗) の間は切替を disabled にして理由を出す。 全置換のため、
 *   未取得のまま送ると他の項目を既定値で上書きする (D6。 送信自体も `usePersonalSettings` が止める)。
 * - 保存成功の toast は出さない (板では左右の入れ替わり自体が結果)。 失敗時は向きを
 *   元に戻して error toast (D11)。
 */
export function useBoardColumnOrder(): UseBoardColumnOrderResult {
  const { t } = useTranslation()
  const errorToast = useErrorToast()
  const onSaveError = useCallback(() => {
    errorToast({ title: t('page:settings.personal.saveError') })
  }, [errorToast, t])

  const {
    settings,
    orderBookColumnOrder,
    isPending,
    updateOrderBookColumnOrder,
  } = usePersonalSettings({
    throwOnError: false,
    notifySaveSuccess: false,
    onSaveError,
  })

  const value: BoardColumnOrder =
    orderBookColumnOrder === OrderBookColumnOrder.BUY_LEFT
      ? 'buyLeft'
      : 'sellLeft'

  const disabledReason =
    settings !== undefined
      ? undefined
      : isPending
        ? t('page:biddingIntraday.market.board.columnOrder.loadingHint')
        : t('page:biddingIntraday.market.board.columnOrder.unavailableHint')

  const onChange = useCallback(
    (next: BoardColumnOrder) => {
      updateOrderBookColumnOrder(
        next === 'buyLeft'
          ? OrderBookColumnOrder.BUY_LEFT
          : OrderBookColumnOrder.SELL_LEFT,
      )
    },
    [updateOrderBookColumnOrder],
  )

  return {
    columnOrder: { value, onChange, disabledReason, isSettling: isPending },
  }
}
